import { ROUTES } from "../../routes";
import styles from "./Landing.module.css";
import { useNavigate } from "react-router-dom";
import { ContentSection, ScrollableLandingPage } from "./ScrollableLandingPage";
import dashboardImageTemp from "./dashboardLight.png";
import attachmentsImage from "./attachmentsLight.png";
import { Message } from "primereact/message";

const LANDING_CONSTANTS = {
  Y_OFFSET: 100,
  DURATION: 0.65,
  EASE: "easeOut",
  MARGIN_1: "-100px",
  MARGIN_2: "-200px",
};

const CustomAppetite = () => {
  return (
    <div className={styles.appetiteContainer}>
      <div className={styles.appetiteRow}>
        <div className={styles.blurContent}>
          <Message text="Favorable Location" severity={"success"} />
          <Message text="High Premium for TIV" severity={"success"} />
          <Message text="Trusted Broker" severity={"success"} />
        </div>
      </div>
      <div className={styles.appetiteRow}>
        <div className={styles.blurContent}>
          <Message text="High Deductible" severity={"success"} />
          <Message text="Non-Clustered Locations" severity={"success"} />
          <Message text="Favorable NAICS Code" severity={"success"} />
        </div>
      </div>
      <div className={styles.appetiteRow}>
        <div className={styles.blurContent}>
          <Message text="Low Deductible" severity={"error"} />
          <Message text="Tier 1 Windzone" severity={"error"} />
          <Message text="New Business" severity={"error"} />
        </div>
      </div>
    </div>
  );
};

export const Product: React.FC<{ withInitialAnimation: boolean }> = ({
  withInitialAnimation,
}) => {
  const navigate = useNavigate();

  const sections: ContentSection[] = [
    {
      id: "solution-header",
      type: "text",
      isFinalComponentInRow: true,
      content: {
        header: "ONE SOLUTION FOR ALL YOUR UNDERWRITING NEEDS",
        highlightText: "SOLUTION",
        infoText:
          "Pantheon removes the busy work and allows your underwriters to jump right in. No more tiresome copy/pasting from Excel sheets and ACORD forms.",
      },
      style: { alignItems: "center", textAlign: "center", width: "800px" },
      margin: LANDING_CONSTANTS.MARGIN_1,
    },
    {
      id: "dashboard-image",
      type: "image",
      isFinalComponentInRow: true,
      content: {
        imageSrc: dashboardImageTemp,
        imageClassName: styles.imgFadeOutLarge,
      },
      style: { width: "1200px", marginTop: "-100px" },
      margin: LANDING_CONSTANTS.MARGIN_2,
    },
    {
      id: "extraction",
      type: "text",
      isFinalComponentInRow: true,
      content: {
        header: "POLICY INFO EXTRACTED IN MINUTES",
        highlightText: "MINUTES",
        infoText:
          "Just forward us your broker emails and we'll handle the rest. Your policy submissions will show up in Pantheon extracted and clean within a matter of minutes.",
      },
      style: {
        alignItems: "center",
        textAlign: "center",
        width: "700px",
        marginBottom: "-100px",
      },
      margin: LANDING_CONSTANTS.MARGIN_1,
    },
    {
      id: "box-1",
      type: "component",
      content: {
        component: <Message text="Broker Info" severity="success" />,
      },
      margin: LANDING_CONSTANTS.MARGIN_1,
      style: { width: "auto" },
    },
    {
      id: "box-2",
      type: "component",
      content: {
        component: <Message text="Company Info" severity="success" />,
      },
      margin: "-110px",
      style: { width: "auto" },
    },
    {
      id: "box-3",
      type: "component",
      content: {
        component: <Message text="ACORD Data" severity="success" />,
      },
      margin: "-120px",
      style: { width: "auto" },
    },
    {
      id: "box-4",
      type: "component",
      isFinalComponentInRow: true,
      content: {
        component: <Message text="SOV Data" severity="success" />,
      },
      margin: "-130px",
      style: { width: "auto" },
    },
    {
      id: "dashboard-text",
      type: "text",
      content: {
        header: "AI WITH ACCOUNTABILITY",
        highlightText: "ACCOUNTABILITY",
        infoText: [
          "Data extraction shouldn't be a mystery.",
          "At Pantheon we show you exactly how we extracted our information. If we aren't confident, we'll let you know.",
        ],
      },
      margin: LANDING_CONSTANTS.MARGIN_1,
    },
    {
      id: "dashboard-image",
      type: "image",
      isFinalComponentInRow: true,
      content: {
        imageSrc: attachmentsImage,
        imageClassName: styles.imgFadeOut,
      },
      margin: LANDING_CONSTANTS.MARGIN_2,
    },
    {
      id: "custom-appetite-image",
      type: "component",
      content: {
        component: <CustomAppetite />,
      },
      layout: "reverse",
      margin: LANDING_CONSTANTS.MARGIN_2,
    },
    {
      id: "custom-appetite-text",
      type: "text",
      isFinalComponentInRow: true,
      content: {
        header: "SET CUSTOM APPETITE GUIDELINES",
        highlightText: "APPETITE",
        infoText: [
          "Avoid wasting time on policies you'll never write. Sort the good ones to the top using policy information like TIV, location, business code, deductibles, and more.",
          "The more policies you bind, the better we'll get at identifying submissions that align with your appetite.",
        ],
      },
      layout: "reverse",
      margin: LANDING_CONSTANTS.MARGIN_1,
    },
    {
      id: "contact-cta",
      type: "cta",
      isFinalComponentInRow: true,
      content: {
        header: "GET STARTED TODAY",
        highlightText: "TODAY",
        infoText:
          "Ready to reach out and see what Pantheon can do for your business? We are always open to building custom solutions and would love to chat.",
        button: {
          label: "GET STARTED",
          outlined: true,
          className: styles.contactButton,
          onClick: () => navigate(ROUTES.CONTACT),
        },
      },
      style: { alignItems: "center", textAlign: "center" },
      margin: LANDING_CONSTANTS.MARGIN_1,
    },
  ];

  return (
    <ScrollableLandingPage
      withInitialAnimation={withInitialAnimation}
      sections={sections}
      styles={styles}
      constants={LANDING_CONSTANTS}
      requireScrollToShowComponents={false}
      gap={144}
    />
  );
};
